import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { useTranslation } from "next-i18next";
import { usePostHog } from "posthog-js/react";
import { useRouter } from "next/router";

import { BodyFrame } from "common/ui/BodyFrame";
import { Heading } from "common/ui/Heading";
import { Stack } from "common/ui/Stack";
import { Paragraph } from "common/ui/Paragraph";
import { Button } from "common/ui/Button";

import { OWNSPACE_INFO } from "common/constants/variables";

import styles from "./ErrorBoundary.module.scss";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  const posthog = usePostHog();

  const logError = (error: Error) => {
    if (process.env.NODE_ENV === "development") return;

    posthog?.captureException(error);
  }

  return (
    <ReactErrorBoundary
      FallbackComponent={Error}
      onError={logError}
    >
      {children}
    </ReactErrorBoundary>);
}

const Error = () => {
  const { t } = useTranslation("common");
  const router = useRouter();

  return (
    <BodyFrame className={styles.ErrorBoundary}>
      <div className={styles.Content}>
        <Heading variant='h1'>
          {t("errorBoundary.oops")}
        </Heading>
        <Paragraph variant="p1">
          {t("errorBoundary.sorry")}
        </Paragraph>
        <Stack direction="column" alignItems="center" gap={24}>
          <Button
            onClick={() => router.reload()}
            variant="primary"
          >
            {t("errorBoundary.refresh")}
          </Button>
          <Button
            className={styles.ContactUsButton}
            variant="tertiary"
            onClick={() => location.href = `mailto:${OWNSPACE_INFO.SUPPORT_EMAIL}`}
          >
            {t("errorBoundary.contactUs")}
          </Button>
        </Stack>
      </div>
    </BodyFrame>
  )
}
